import {
  isClassificationInFlight,
  type DocumentCategory,
  type DocumentCategorySource,
  type DocumentClassificationStatus,
} from '@immo/api-contract';
import { StatusBadge } from '@immo/ui-kit';
import { useTranslations } from '@/i18n/use-translations';

const DETAIL_NS = 'pages.admin.valuations.detail';

export type DocumentClassificationBadgeProps = {
  readonly category: DocumentCategory | null;
  readonly categorySource: DocumentCategorySource | null;
  readonly classificationStatus: DocumentClassificationStatus | null;
};

/**
 * The Objekt's category for one attached Unterlage, as the operator sees it
 * (IMM-501.3) — deliberately the same four states, labels and markers the agent's
 * Unterlagen list shows, because both surfaces read the same classification.
 *
 * A null `category` renders nothing at all: a legacy attachment, an image, or a
 * reference whose Objekt document no longer resolves. That is a different fact
 * from `UNCATEGORIZED`, which says immoOS read the document and could not place
 * it — a signal to open it — so the two must not collapse into one marker.
 */
export function DocumentClassificationBadge({
  category,
  categorySource,
  classificationStatus,
}: DocumentClassificationBadgeProps) {
  const tCategory = useTranslations(`${DETAIL_NS}.category`);
  const tClassification = useTranslations(`${DETAIL_NS}.classification`);

  if (category === null) return null;

  const isClassifying = isClassificationInFlight(classificationStatus);
  const isAutoCategorized = classificationStatus === 'DONE' && categorySource === 'AUTO';

  // The same three tones the agent's badge uses — now the same COMPONENT, so
  // they cannot drift apart again. They were spelled out here only because the
  // admin app had no badge to reach for.
  const tone = isClassifying ? 'warning' : category === 'UNCATEGORIZED' ? 'neutral' : 'info';

  return (
    <span className="flex min-w-0 flex-col items-end gap-0.5">
      <StatusBadge tone={tone} size="sm" label={isClassifying ? tClassification('sorting') : tCategory(category)} />
      {isAutoCategorized && <span className="text-xs text-text-tertiary">{tClassification('auto-sorted')}</span>}
    </span>
  );
}
